import { useState } from 'react';
import { Screen } from '../../app/Frame';
import { useMeContext, useWritable } from '../../app/MeContext';
import { useScreen, useSheet, useToast } from '../../app/shell-context';
import { ApiError } from '../../api/client';
import { addDaysISO, formatISODay, formatMinutes, parseHHMM, todayISO } from '../../lib/time';
import { Notice, Pill } from '../../ui/parts';
import { useAddException, useDeleteException, useSchedule } from '../api';
import type { Exception } from '../types';

/**
 * Вкладка «Особые дни» — те, что выпадают из обычного расписания. Они сильнее
 * рабочих часов из «Расписания»: закрывают день целиком или, наоборот, открывают
 * время там, где специалист обычно не работает.
 */
export function Off() {
  const me = useMeContext();
  const writable = useWritable();
  const sheet = useSheet();
  const schedule = useSchedule();
  // Прошедшие исключения не показываем: список должен отвечать на вопрос «что впереди».
  const today = todayISO(me.center.tz);
  const list = (schedule.data?.exceptions ?? []).filter((e) => e.date >= today);

  useScreen('Особые дни', null);

  return (
    <Screen>
      <div>
        <p className="eyebrow">Отпуск и особые дни</p>
        <p className="lead" style={{ marginTop: 6 }}>
          Здесь отмечают дни, которые выпадают из обычного расписания: отпуск, отгул
          или наоборот, приём в нерабочий день.
        </p>
      </div>

      {schedule.isPending ? <p className="lead">Загружаем…</p> : null}

      <div className="rows">
        {list.length ? list.map((e) => <ExceptionRow key={e.id} exception={e} writable={writable} />) : null}
        {!schedule.isPending && list.length === 0 ? (
          <p className="lead">Особых дней нет — работаете по обычному расписанию.</p>
        ) : null}
      </div>

      {writable ? (
        <button className="add" onClick={() => sheet.open(<AddExceptionSheet />)}>＋ Отметить день</button>
      ) : null}

      <Notice>
        Записи на эти дни не отменятся автоматически. Перенесите их сами — клиент
        получит уведомление о новом времени.
      </Notice>
    </Screen>
  );
}

function ExceptionRow({ exception, writable }: { exception: Exception; writable: boolean }) {
  const tf = useMeContext().center.timeFormat;
  const toast = useToast();
  const remove = useDeleteException();
  const wholeDay = exception.start_min === null;

  return (
    <div className="rw">
      <span className="rw-body">
        <b>{formatISODay(exception.date)}</b>
        <span>
          {wholeDay ? 'весь день' : `${formatMinutes(exception.start_min!, tf)} – ${formatMinutes(exception.end_min ?? 0, tf)}`}
          {exception.comment ? ` · ${exception.comment}` : ''}
        </span>
      </span>
      {exception.kind === 'off' ? <Pill kind="off">закрыто</Pill> : <Pill kind="paid">открыто</Pill>}
      {writable ? (
        <button
          className="chev"
          aria-label="Удалить"
          disabled={remove.isPending}
          onClick={() =>
            remove.mutate(exception.id, {
              onSuccess: () => toast('Отметка снята'),
              onError: (e) => toast(e instanceof ApiError ? e.message : 'Не получилось удалить'),
            })
          }
        >
          <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
            <path d="M6 6l12 12M18 6L6 18" />
          </svg>
        </button>
      ) : null}
    </div>
  );
}

function AddExceptionSheet() {
  const me = useMeContext();
  const sheet = useSheet();
  const toast = useToast();
  const add = useAddException();

  const [date, setDate] = useState(() => addDaysISO(todayISO(me.center.tz), 1));
  const [kind, setKind] = useState<'off' | 'extra'>('off');
  const [wholeDay, setWholeDay] = useState(true);
  const [from, setFrom] = useState('10:00');
  const [to, setTo] = useState('14:00');
  const [comment, setComment] = useState('');

  // «Открыто» без часов бессмысленно: открывать надо конкретный интервал.
  const needsRange = kind === 'extra' || !wholeDay;
  const ok = date && (!needsRange || parseHHMM(to) > parseHHMM(from));

  return (
    <>
      <h3>Особый день</h3>

      {/* Выбор идёт первым: от него зависит, нужен ли интервал и что вообще спрашивать дальше. */}
      <div className="field">
        <label>Что делаем</label>
        <div className="chips">
          <button className="chip" aria-pressed={kind === 'off'} onClick={() => setKind('off')}>Не работаю</button>
          <button className="chip" aria-pressed={kind === 'extra'} onClick={() => { setKind('extra'); setWholeDay(false); }}>
            Работаю дополнительно
          </button>
        </div>
        {/* Оба пояснения видны сразу: они помогают сделать выбор, а не подтверждают сделанный. */}
        <span className="hint">Не работаю: записаться в этот день будет нельзя, даже если он есть в вашем расписании.</span>
        <span className="hint">Работаю дополнительно: откроем время для записи в день, когда вы обычно не принимаете.</span>
      </div>

      <div className="field">
        <label htmlFor="exDate">Дата</label>
        <input id="exDate" type="date" value={date} min={todayISO(me.center.tz)} onChange={(e) => setDate(e.target.value)} />
      </div>

      {kind === 'off' ? (
        <div className="field">
          <label className="check">
            <input type="checkbox" checked={wholeDay} onChange={(e) => setWholeDay(e.target.checked)} />
            <span>Весь день</span>
          </label>
          <span className="hint">Снимите — появятся поля «с» и «до».</span>
        </div>
      ) : null}

      {needsRange ? (
        <div className="two">
          <div className="field">
            <label htmlFor="exFrom">С</label>
            <input id="exFrom" type="time" value={from} onChange={(e) => setFrom(e.target.value)} />
          </div>
          <div className="field">
            <label htmlFor="exTo">До</label>
            <input id="exTo" type="time" value={to} onChange={(e) => setTo(e.target.value)} />
          </div>
        </div>
      ) : null}

      <div className="field">
        <label htmlFor="exComment">Причина (необязательно)</label>
        <input id="exComment" type="text" placeholder="Например: отпуск" value={comment} onChange={(e) => setComment(e.target.value)} />
        <span className="hint">Видно только вам и администратору.</span>
      </div>

      <div className="row-actions">
        <button className="btn" onClick={() => sheet.close()}>Отмена</button>
        <button
          className="btn solid"
          disabled={!ok || add.isPending}
          onClick={() =>
            add.mutate(
              {
                date,
                kind,
                start_min: needsRange ? parseHHMM(from) : null,
                end_min: needsRange ? parseHHMM(to) : null,
                comment: comment.trim(),
              },
              {
                onSuccess: () => {
                  toast('День отмечен');
                  sheet.close();
                },
                onError: (e) => toast(e instanceof ApiError ? e.message : 'Не получилось добавить'),
              },
            )
          }
        >
          Сохранить
        </button>
      </div>
    </>
  );
}
